/**
 * Decision trees per typology — aligned with SVCP_DecisionTrees.md
 * Each tree: { nodeId: { phase, question, why?, context?, options: [{ label, hint?, next }], terminal?, outcome?, title?, summary?, colour?, Icon? } }
 */

import { CheckCircle2, Shield, FileText, AlertTriangle } from 'lucide-react'

const C = {
  pri: '#0a5169', priM: '#e8f2f6',
  red: '#dc2626', redBg: '#fef2f2', redBd: '#fecaca',
  amber: '#d97706', amberBg: '#fffbeb', amberBd: '#fde68a',
  green: '#16a34a', greenBg: '#f0fdf4', greenBd: '#bbf7d0',
  purple: '#7c3aed', purpleBg: '#f5f3ff', purpleBd: '#ddd6fe',
  slate: '#64748b', slateL: '#94a3b8',
  ink: '#0f172a',
}

function buildStructuringTree(patron, transactions, fmt$) {
  const name = patron?.name || 'Patron'
  const occupation = patron?.occupation || '—'
  const risk = patron?.risk ?? 0
  const txTotal = transactions.reduce((a, t) => a + (t.amount || 0), 0)
  const fmt = fmt$ || ((n) => '$' + (n ?? 0).toLocaleString())
  const txSummary = transactions.length ? `${transactions.length} txns, ${fmt(txTotal)}` : 'Review linked transactions'
  return {
    root: {
      phase: 'Initial Filter',
      question: 'Do the individual payout amounts cluster just below the $10,000 TTR threshold?',
      why: 'This is the primary diagnostic for structuring. Clustering near that boundary suggests threshold awareness.',
      context: `${txSummary}. Structuring keeps each transaction below $10,000 to avoid automatic reporting.`,
      options: [
        { label: 'No — amounts well below threshold, no clustering pattern', hint: 'Structuring less likely. Explore velocity and profile mismatch.', next: 'no_clustering' },
        { label: 'Yes — amounts near $10k or obvious split pattern visible', hint: 'Classic structuring indicator.', next: 'yes_clustering' },
        { label: 'Unclear — volume concerning but no clear threshold pattern', hint: 'Consider whether velocity itself is the issue.', next: 'velocity_focus' },
      ],
    },
    no_clustering: {
      phase: 'Volume Assessment',
      question: "Is the cumulative payout volume materially inconsistent with this patron's declared income or occupation?",
      why: 'If structuring is not the concern, the next question is whether the volume is suspicious relative to what this person plausibly has to spend.',
      context: `${name} — ${occupation}. Risk: ${risk}/100. Review patterns and KYC data.`,
      options: [
        { label: 'Yes — payouts significantly exceed plausible income', hint: 'Profile mismatch path — source of funds scrutiny required.', next: 'outcome_ecdd' },
        { label: 'No — activity consistent with patron profile and history', hint: 'Low-risk path. Consider closing.', next: 'outcome_close' },
        { label: 'Cannot assess — insufficient income information', hint: 'ECDD may be needed to gather information.', next: 'outcome_ecdd' },
      ],
    },
    velocity_focus: {
      phase: 'Velocity Analysis',
      question: 'Has this patron shown a similar velocity pattern on prior visits — or is this an isolated incident?',
      why: 'A single high-velocity day could be luck. A recurring pattern across visits suggests deliberate behaviour.',
      options: [
        { label: 'Yes — similar velocity pattern in prior visits', hint: 'Pattern strengthens suspicion.', next: 'outcome_ecdd' },
        { label: 'No — isolated high-activity event', hint: 'Could be jackpot or bonus. Check gaming records.', next: 'outcome_close' },
      ],
    },
    yes_clustering: {
      phase: 'Structuring — EFT Test',
      question: 'Did the patron refuse or resist electronic payment (EFT or cheque) on this or prior occasions?',
      why: 'Insisting on cash when alternatives are available is a strong structuring indicator.',
      options: [
        { label: 'Yes — EFT refusal documented', hint: 'Significant indicator. Continue building the case.', next: 'outcome_smr' },
        { label: 'No — patron accepted alternatives', hint: 'Weakens structuring hypothesis.', next: 'outcome_ecdd' },
      ],
    },
    outcome_close: { terminal: true, outcome: 'close', title: 'Close — No Further Action', summary: 'Activity adequately explained.', colour: C.green, Icon: CheckCircle2 },
    outcome_ecdd: { terminal: true, outcome: 'ecdd', title: 'Open / Continue ECDD', summary: 'Enhanced due diligence required.', colour: C.purple, Icon: Shield },
    outcome_smr: { terminal: true, outcome: 'smr', title: 'File SMR', summary: 'Suspicion formed — report to AUSTRAC.', colour: C.red, Icon: FileText },
  }
}

function buildVelocityTree(patron, transactions, fmt$) {
  const fmt = fmt$ || ((n) => '$' + (n ?? 0).toLocaleString())
  const txTotal = transactions.reduce((a, t) => a + (t.amount || 0), 0)
  const txSummary = transactions.length ? `${transactions.length} txns, ${fmt(txTotal)}` : 'Review linked transactions'
  return {
    root: {
      phase: 'Velocity Trigger',
      question: 'Are payouts spread across multiple EGMs in the same session?',
      why: 'Multi-EGM collection without play suggests cash cycling.',
      context: txSummary,
      options: [
        { label: 'Yes — payouts from multiple EGMs', hint: 'Check if patron genuinely played each.',
          next: 'multi_egm' },
        { label: 'No — single EGM', hint: 'Session data should confirm genuine play.',
          next: 'single_egm' },
      ],
    },
    multi_egm: {
      phase: 'Play vs Collection',
      question: 'Did the patron genuinely PLAY each EGM, or collect payouts without play?',
      why: 'Collecting without play indicates cash cycling.',
      options: [
        { label: 'Played — session data confirms genuine play', hint: 'Volume may still be unusual vs baseline.', next: 'volume_baseline' },
        { label: 'Collected without play', hint: 'Highly suspicious.', next: 'outcome_smr' },
      ],
    },
    single_egm: {
      phase: 'Win Confirmation',
      question: 'Does session data confirm a genuine win?',
      why: 'Single EGM with confirmed win — volume explained.',
      options: [
        { label: 'Yes — win confirmed', hint: 'Volume explained.', next: 'outcome_close' },
        { label: 'No — no win but collecting', hint: 'Suspicious.', next: 'outcome_smr' },
      ],
    },
    volume_baseline: {
      phase: 'Volume vs Baseline',
      question: 'Is the payout volume unusual compared to this patron\'s historical baseline?',
      why: 'Spike vs baseline may warrant SOF request.',
      options: [
        { label: 'Yes — significant escalation', hint: 'Request SOF/ECDD.', next: 'outcome_ecdd' },
        { label: 'No — within patron\'s normal range', hint: 'Document and close.', next: 'outcome_close' },
      ],
    },
    outcome_close: { terminal: true, outcome: 'close', title: 'Close — No Further Action', summary: 'Activity adequately explained.', colour: C.green, Icon: CheckCircle2 },
    outcome_ecdd: { terminal: true, outcome: 'ecdd', title: 'Open / Continue ECDD', summary: 'Enhanced due diligence required.', colour: C.purple, Icon: Shield },
    outcome_smr: { terminal: true, outcome: 'smr', title: 'File SMR', summary: 'Suspicion formed — report to AUSTRAC.', colour: C.red, Icon: FileText },
  }
}

function buildThirdPartyTree() {
  return {
    root: {
      phase: 'Third-Party Observation',
      question: 'Is the collector the same person who played? (Confirm via CCTV or staff recognition)',
      why: 'Different person collecting is the core trigger.',
      options: [
        { label: 'Yes — same person', hint: 'No third-party issue.', next: 'outcome_close' },
        { label: 'No — different person collected', hint: 'Check for authorisation.', next: 'authorised' },
      ],
    },
    authorised: {
      phase: 'Authorisation',
      question: 'Is there documented authorisation? (carer, family, power of attorney)',
      why: 'Undocumented third-party collection is highly suspicious.',
      options: [
        { label: 'Yes — documented', hint: 'One-off or pattern?', next: 'pattern_check' },
        { label: 'No — undocumented', hint: 'Preserve CCTV. Highly suspicious.', next: 'outcome_smr' },
      ],
    },
    pattern_check: {
      phase: 'Pattern Check',
      question: 'Is this a one-off or recurring pattern?',
      why: 'Authorised but repeated pattern may still be suspicious.',
      options: [
        { label: 'One-off', hint: 'Document and monitor.', next: 'outcome_close' },
        { label: 'Recurring pattern', hint: 'Escalate.', next: 'outcome_smr' },
      ],
    },
    outcome_close: { terminal: true, outcome: 'close', title: 'Close — No Further Action', summary: 'Documented or one-off.', colour: C.green, Icon: CheckCircle2 },
    outcome_smr: { terminal: true, outcome: 'smr', title: 'File SMR', summary: 'Suspicion formed — report to AUSTRAC.', colour: C.red, Icon: FileText },
  }
}

function buildEftRefusalTree() {
  return {
    root: {
      phase: 'EFT Refusal',
      question: 'Has the patron refused EFT on multiple occasions?',
      why: 'Pattern of refusal is stronger than one-off.',
      options: [
        { label: 'Once / rare', hint: 'Document preference, no action.', next: 'outcome_close' },
        { label: 'Yes — pattern of refusal', hint: 'Assess reason.', next: 'reason_check' },
      ],
    },
    reason_check: {
      phase: 'Reason Check',
      question: 'Is there a plausible reason? (e.g. no bank account)',
      why: 'Unbanked patrons may legitimately prefer cash.',
      options: [
        { label: 'Yes — plausible (e.g. no bank account)', hint: 'Document and monitor.', next: 'outcome_close' },
        { label: 'Vague / refuses to explain', hint: 'Does patron have bank account on profile?', next: 'account_check' },
      ],
    },
    account_check: {
      phase: 'Bank Account',
      question: 'Does the patron have a bank account on file?',
      why: 'Refusal despite having account is suspicious.',
      options: [
        { label: 'Yes — refusal despite account', hint: 'Suspicious.', next: 'outcome_smr' },
        { label: 'No / unknown', hint: 'Unbanked may be legitimate.', next: 'outcome_ecdd' },
      ],
    },
    outcome_close: { terminal: true, outcome: 'close', title: 'Close — No Further Action', summary: 'Preference documented.', colour: C.green, Icon: CheckCircle2 },
    outcome_ecdd: { terminal: true, outcome: 'ecdd', title: 'Open / Continue ECDD', summary: 'Enhanced due diligence required.', colour: C.purple, Icon: Shield },
    outcome_smr: { terminal: true, outcome: 'smr', title: 'File SMR', summary: 'Suspicion formed — report to AUSTRAC.', colour: C.red, Icon: FileText },
  }
}

function buildSofMismatchTree(patron) {
  const name = patron?.name || 'Patron'
  const occupation = patron?.occupation || '—'
  return {
    root: {
      phase: 'KYC Profile',
      question: 'Does the KYC profile contain occupation / SOF information?',
      why: 'Without SOF, we cannot assess consistency.',
      context: `${name} — ${occupation}`,
      options: [
        { label: 'Yes', hint: 'Check consistency.', next: 'consistency_check' },
        { label: 'No', hint: 'ECDD trigger — request SOF.', next: 'sof_request' },
      ],
    },
    consistency_check: {
      phase: 'Consistency',
      question: 'Is gaming volume consistent with declared SOF?',
      why: 'Inconsistency warrants SOF request.',
      options: [
        { label: 'Yes', hint: 'Close.', next: 'outcome_close' },
        { label: 'No — inconsistent', hint: 'Request SOF formally.', next: 'sof_request' },
      ],
    },
    sof_request: {
      phase: 'SOF Request',
      question: 'Has SOF been provided?',
      why: 'Refusal or non-response is itself an indicator.',
      options: [
        { label: 'Provided — and explains volume', hint: 'Close.', next: 'outcome_close' },
        { label: 'Provided — does not explain', hint: 'Suspicion formed.', next: 'outcome_smr' },
        { label: 'Not provided / refused', hint: 'Suspicion formed.', next: 'outcome_smr' },
      ],
    },
    outcome_close: { terminal: true, outcome: 'close', title: 'Close — No Further Action', summary: 'SOF documented.', colour: C.green, Icon: CheckCircle2 },
    outcome_smr: { terminal: true, outcome: 'smr', title: 'File SMR', summary: 'Suspicion formed — report to AUSTRAC.', colour: C.red, Icon: FileText },
  }
}

function buildPepSanctionsTree() {
  return {
    root: {
      phase: 'Screening Hit',
      question: 'Is the screening hit CONFIRMED or POSSIBLE?',
      why: 'PEP requires ECDD regardless. Sanctions requires immediate MLRO contact.',
      options: [
        { label: 'Confirmed', hint: 'Check if sanctions.', next: 'sanctions_check' },
        { label: 'Possible match', hint: 'Disambiguation required.', next: 'outcome_ecdd' },
      ],
    },
    sanctions_check: {
      phase: 'Sanctions Check',
      question: 'Is the patron on a SANCTIONS list?',
      why: 'Sanctions = critical. Contact MLRO immediately.',
      options: [
        { label: 'Yes', hint: 'Contact MLRO now. SMR.',
          next: 'outcome_smr' },
        { label: 'No — PEP only', hint: 'Mandatory ECDD. Activity check.',
          next: 'activity_check' },
      ],
    },
    activity_check: {
      phase: 'Activity Level',
      question: 'Is gaming activity above LOW threshold?',
      why: 'PEP requires ECDD regardless. High activity may also raise suspicion.',
      options: [
        { label: 'Yes — above low', hint: 'Full ECDD. Does activity raise suspicion?', next: 'suspicion_check' },
        { label: 'No — minimal activity', hint: 'PEP on file. ECDD required. Annual review.', next: 'outcome_ecdd' },
      ],
    },
    suspicion_check: {
      phase: 'Suspicion Beyond PEP',
      question: 'Does activity raise suspicion beyond PEP status?',
      why: 'ECDD and SMR run in parallel for PEPs.',
      options: [
        { label: 'Yes', hint: 'ECDD + SMR parallel.', next: 'outcome_smr' },
        { label: 'No', hint: 'PEP on file, ECDD active.', next: 'outcome_ecdd' },
      ],
    },
    outcome_ecdd: { terminal: true, outcome: 'ecdd', title: 'ECDD Active', summary: 'PEP on file. ECDD required. Annual review.', colour: C.purple, Icon: Shield },
    outcome_smr: { terminal: true, outcome: 'smr', title: 'File SMR', summary: 'Suspicion formed — report to AUSTRAC.', colour: C.red, Icon: FileText },
  }
}

const TREE_BUILDERS = {
  structuring: buildStructuringTree,
  velocity: buildVelocityTree,
  third_party: buildThirdPartyTree,
  eft_refusal: buildEftRefusalTree,
  sof_mismatch: buildSofMismatchTree,
  pep_sanctions: buildPepSanctionsTree,
}

export function buildTreeForTypology(typology, patron, transactions, fmt$) {
  const builder = TREE_BUILDERS[typology] || buildStructuringTree
  return builder(patron, transactions, fmt$)
}
